import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import dayjs from "dayjs";

import CalculationResultAttribute from "./CalculationResultAttribute";

import { useClient } from "../../../context/ClientProvider";
import { useUtils } from "../../../context/UtilsProvider";
import { useLiveLoans } from "../../../context/LiveLoansProvider";
import { useAnalytics } from "../../../context/AnalyticsProvider";
import { formatCurrency } from '../../../utils/formatCurrency';

function CalculationResult({ liveAccount, proposedDate, response, setPaymentDateChangeSaved }) {

  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();
  const { setTag } = useAnalytics();
  const { createAlert, alertSeverityTypes } = useUtils();

  const { postPaymentDateChange } = useLiveLoans();

  const [additionalPayment, setAdditionalPayment] = useState(0.0);
  const [isPaymentDateChangeSaving, setIsPaymentDateChangeSaving] = useState(false);

  async function savePaymentDateChange() {
    const payload = {
      "RequestedDate": dayjs(proposedDate).format("YYYY-MM-DD"),
      "CurrentDueDate": liveAccount?.contractualDueDate,
      "CurrentNextDueDate": liveAccount?.nextDueDate,
      "NewDueDate": response?.newDueDate,
      "NewNextDueDate": response?.newNextDueDate,
      "CurrentMaturityDate": liveAccount?.contractEndDate,
      "NewMaturityDate": response?.newMaturityDate,
      "CurrentAmountDue": liveAccount?.amountDue,
      "NewAmountDue": response?.newAmountDue,
      "CreatedWhen": null,
      "CreatedBy": null,
      "ModifiedWhen": null,
      "ModifiedBy": null,
      "CurrentFixedCycleDay": liveAccount?.fixedCycleDay,
      "NewFixedCycleDay": response?.newFixedCycleDay,
      "OptionPeriodDate": null,
      "Active": true
    };

    try {
      setIsPaymentDateChangeSaving(true);
      await postPaymentDateChange(payload);
      setIsPaymentDateChangeSaving(false);
      createAlert("Payment date change successful.", alertSeverityTypes.success);
      setPaymentDateChangeSaved(true);
      setTag("pplan_date_change");
    } catch (error) {
      setIsPaymentDateChangeSaving(false);
      console.error("Failed to save payment date change:", error);
      createAlert("There was a problem. Please try again.", alertSeverityTypes.error);
      throw error;
    }
  }


  useEffect(() => {
    if (response && liveAccount) {
      const newAdditionalPayment = response?.newAmountDue - liveAccount?.amountDue;
      setAdditionalPayment(Number(newAdditionalPayment.toFixed(2)));
      console.log(newAdditionalPayment)
    }
  }, [response, liveAccount]);

  return (liveAccount && clientConfig) && (
    <>
      <Box>
        {additionalPayment > 0 && (
          <Typography sx={{ fontSize: 16, mb: 2 }}>
            {`Your next repayment amount will include an additional ${formatCurrency(additionalPayment)}.  
              This is a one-off amount increase to cover your additional interest.`}
          </Typography>
        )}
      </Box>

      <Box sx={{ width: { xs: "100%", sm: "50%" }, pt: 1 }}>
        <Box sx={{ pt: 1 }}>
          <CalculationResultAttribute
            id="new-repayment-date"
            title="New repayment date:"
            value={dayjs(response?.newDueDate).format("DD/MM/YYYY")}
          />
        </Box>
        <Box sx={{ pt: 1 }}>
          <CalculationResultAttribute
            id="new-next-repayment-due-date"
            title="New next repayment due date:"
            value={dayjs(response?.newNextDueDate).format("DD/MM/YYYY")}
          />
        </Box>
        <Box sx={{ pt: 1 }}>
          <CalculationResultAttribute
            id="next-repayment-amount"
            title="Next repayment amount:"
            value={formatCurrency(response?.newAmountDue)}
          />
        </Box>
        <Box sx={{ pt: 1 }}>
          <CalculationResultAttribute
            id="regular-repayment-amount"
            title="Regular repayment amount:"
            value={formatCurrency(liveAccount?.amountDue)}
          />
        </Box>
        <Box sx={{ pt: 1 }}>
          <CalculationResultAttribute
            id="new-loan-term-end-date"
            title="New loan term end date:"
            value={dayjs(response?.newMaturityDate).format("DD/MM/YYYY")}
          />
        </Box>
      </Box>

      <Box sx={{ pt: 5 }}>
        <Button
          id="save-payment-date-change-btn"
          onClick={savePaymentDateChange}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: clientConfig?.layout?.buttonBorderRadius,
            width: { xs: "15em", lg: "20em" },
            backgroundColor: clientConfig?.layout?.primaryButtonBackgroundColour,
            color: clientConfig?.layout?.primaryButtonColour,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            "&:hover": {
              backgroundColor: clientConfig?.layout?.hoverPrimaryButtonBackgroundColour,
              color: clientConfig?.layout?.hoverPrimaryButtonColour
            }
          }}
        >
          Save
        </Button>
      </Box>

      <Box sx={{ position: "relative", mt: 4 }}>
        {isPaymentDateChangeSaving && (<CircularProgress size={24} />)}
      </Box>
    </>
  );
}

CalculationResult.propTypes = {
  liveAccount: PropTypes.object.isRequired,
  proposedDate: PropTypes.string.isRequired,
  response: PropTypes.object.isRequired,
  setPaymentDateChangeSaved: PropTypes.func.isRequired,
};

export default CalculationResult;
